import { FC } from 'react';

import { useRouter } from 'next/router';

import { RiCalendarCloseLine, RiShieldCheckLine } from 'react-icons/ri';

import { routes } from '~/shared/constants/routes';
import { Image } from '~/shared/components/Image';
import { Button } from '~/shared/components/Form/Button';

import {
  IPraNotasCertificateAlert,
  PraNotasCertificateAlertSeverity,
} from '../../interfaces/IPraNotasCertificateAlert';

import {
  Actions,
  Container,
  Content,
  Header,
  HeaderIcon,
  HeaderText,
  Hint,
  IllustrationImageWrapper,
  LeftColumn,
  RightColumn,
  SectionTitle,
  SummaryBox,
} from './styles';

interface SeverityTheme {
  accent: string;
  summaryBackground: string;
  summaryBorder: string;
  imageSrc: string;
}

const severityThemeMap: Record<
  Exclude<PraNotasCertificateAlertSeverity, 'none'>,
  SeverityTheme
> = {
  info: {
    accent: '#01739D',
    summaryBackground: '#CDE3EC',
    summaryBorder: '#01739D',
    imageSrc: '/images/pra-notas-alerts/certificate-alert-info.png',
  },
  warning: {
    accent: '#FB6340',
    summaryBackground: '#FEE0D9',
    summaryBorder: '#FB6340',
    imageSrc: '/images/pra-notas-alerts/certificate-alert-warning.png',
  },
  critical: {
    accent: '#F5365C',
    summaryBackground: '#FDD7DE',
    summaryBorder: '#F5365C',
    imageSrc: '/images/pra-notas-alerts/certificate-alert-critical.png',
  },
  expired: {
    accent: '#F5365C',
    summaryBackground: '#FDD7DE',
    summaryBorder: '#F5365C',
    imageSrc: '/images/pra-notas-alerts/certificate-alert-critical.png',
  },
};

interface PraNotasCertificatesAlertModalProps {
  alerts: IPraNotasCertificateAlert[];
  highestSeverity: Exclude<PraNotasCertificateAlertSeverity, 'none'>;
  hasExpired: boolean;
  onDismiss: () => void;
  handleClose: () => void;
}

export const PraNotasCertificatesAlertModal: FC<
  PraNotasCertificatesAlertModalProps
> = ({ alerts, highestSeverity, hasExpired, onDismiss, handleClose }) => {
  const router = useRouter();
  const companiesCount = alerts.length;
  const theme = severityThemeMap[highestSeverity];

  const handleManageCertificates = (): void => {
    onDismiss();
    handleClose();
    router.push(routes.apps.settings('pra-notas'));
  };

  const handleRememberLater = (): void => {
    onDismiss();
    handleClose();
  };

  return (
    <Container>
      <Header>
        <HeaderIcon accent={theme.accent}>
          <RiCalendarCloseLine />
        </HeaderIcon>

        <HeaderText>
          <h1>
            Atenção: existem certificados digitais que precisam de atenção
          </h1>
          <p>
            Algumas empresas da sua conta possuem certificados
            {hasExpired ? ' vencidos ou ' : ' '}próximos do vencimento. Antecipe
            a renovação e evite interrupções na emissão fiscal.
          </p>
        </HeaderText>
      </Header>

      <Content>
        <LeftColumn>
          <SectionTitle>Resumo da sua conta</SectionTitle>

          <SummaryBox
            backgroundColor={theme.summaryBackground}
            borderColor={theme.summaryBorder}
          >
            {companiesCount} empresa{companiesCount > 1 ? 's' : ''} da sua conta{' '}
            {companiesCount > 1 ? 'possuem' : 'possui'} certificados
            {hasExpired ? ' vencidos ou ' : ' '}próximos do vencimento.
          </SummaryBox>

          <Hint accent={theme.accent}>
            <RiShieldCheckLine />
            <p>
              Evite o bloqueio de NF-e e NFC-e renovando seu certificado com
              antecedência.
            </p>
          </Hint>

          <Actions>
            <Button
              containerStyle={{ width: 'auto' }}
              onClick={handleManageCertificates}
            >
              Gerenciar certificados
            </Button>

            <Button
              variant="outlined"
              backgroundColor="background"
              textColor="info"
              borderColor="info"
              containerStyle={{ width: 'auto' }}
              onClick={handleRememberLater}
            >
              Lembrar depois
            </Button>
          </Actions>
        </LeftColumn>

        <RightColumn>
          <IllustrationImageWrapper>
            <Image
              src={theme.imageSrc}
              alt="Ilustração de alerta de certificado digital"
              width={349}
              height={308}
              objectFit="contain"
            />
          </IllustrationImageWrapper>
        </RightColumn>
      </Content>
    </Container>
  );
};
